import React, { useEffect, useState, createContext, useRef, forwardRef } from "react";
import type { ForwardedRef, ForwardRefExoticComponent, RefAttributes } from "react";
import type {
  ContextFormProps,
  FormContextValue,
  FormData,
  FormAction,
  FormMetadata,
  FormErrors,
  FieldMetadata,
} from "./types";
import PropTypes from "prop-types";
import { useNotificationContext } from "../NotificationContext";
import { useLoadingContext } from "../LoadingContext";
import {
  useFormReviewWarningsContext,
  FormReviewWarningsContextProvider,
} from "../FormReviewWarningsContext";
import useData from "./useData";
import Action, { ActionBar } from "./Action";
import ContextFormField from "./Field";
import { nanoid } from "nanoid";
import useIsInDialog from "../useIsInDialog";

const EMPTY_DATA = {};
const NOOP = () => {};

const DEFAULT_MESSAGES = {
  errorRequired: "Required",
  errorSummary: "Please correct the errors indicated below to submit.",
  errorGeneric: "Something went wrong. Please review the error indicated or try again later.",
  errorCheckInfo: "Double-check your info and submit again to proceed.",
  warnConfirm: "Are you sure you wish to proceed?",
};

// TODO do this better
const isLeafMetadataNode = (node: FieldMetadata | FormMetadata): node is FieldMetadata =>
  ["validate", "required"].every((x) => Object.keys(node).includes(x));

export const doFormValidation = (
  data: FormData,
  metadata: FormMetadata,
  rootData: FormData,
  messages = DEFAULT_MESSAGES
): FormErrors => {
  const errors: FormErrors = {};
  Object.keys(metadata).forEach((field) => {
    const value = data[field] || "";
    if (!isLeafMetadataNode(metadata[field])) {
      errors[field] = doFormValidation(value, metadata[field] as FormMetadata, rootData, messages);
    } else {
      const { required, validate } = metadata[field];
      let errorForField: unknown = undefined;
      if (validate) {
        // in case the field validation requires access to other fields
        errorForField = validate(value, data, rootData);
      }
      if (!errorForField && required && [null, undefined, ""].includes(value)) {
        errorForField = messages.errorRequired;
      }
      errors[field] = errorForField;
    }
  });
  return errors;
};

export const FormContext = createContext<FormContextValue | undefined>(undefined);

const FormContextProvider = ({
  children,
  data = EMPTY_DATA,
  onChange = NOOP,
  formID: formIDProp,
  shouldHideAllToasts = false,
  shouldHideInlineToasts = false,
  messages = DEFAULT_MESSAGES,
}: ContextFormProps & { shouldHideInlineToasts?: boolean }) => {
  const [formID] = useState(formIDProp || nanoid());
  const { data: metadata, dispatchData: dispatchMetadata } = useData() as ReturnType<
    typeof useData
  > & { data: FormMetadata };
  const { dispatchData: dispatchFormData } = useData({
    initialData: data,
  });
  const dispatchWithOnChange: FormContextValue["dispatchData"] = (action) => {
    dispatchFormData(action);
    const update = { [action.field as string]: (action as { update?: unknown }).update };
    onChange(mergeObjects(data, update) as FormData);
  };

  useEffect(() => {
    dispatchFormData({
      type: "update",
      update: data,
    });
  }, [data]);

  const [isSubmitting, setIsSubmitting] = useState<FormAction["label"]>("");
  let { sendNotification, clearNotifications, sendNegativeNotification } = useNotificationContext();
  const { setIsLoading } = useLoadingContext();
  const { handleWarnings } = useFormReviewWarningsContext();

  if (shouldHideAllToasts) {
    sendNotification = () => {};
    clearNotifications = () => {};
  }

  const setFormErrors = (errors: FormErrors) => {
    const update = { ...metadata };
    const setErrors = (m: FormMetadata, e: FormErrors) => {
      Object.keys(m).forEach((field) => {
        if (isLeafMetadataNode(m[field])) {
          m[field].error = e[field] || undefined;
        } else setErrors(m[field] as FormMetadata, (e[field] || {}) as FormErrors);
      });
    };
    setErrors(update, errors);
    dispatchMetadata({
      type: "update",
      update,
    });
  };

  const validateForm = () => {
    const validationErrors = doFormValidation(data, metadata, data, messages);
    setFormErrors(validationErrors);
    let hasError = false;
    const nodeHasError = (node: object) => {
      Object.values(node).forEach((value) => {
        if (value !== null && typeof value === "object") nodeHasError(value);
        else {
          if (!!value) hasError = true;
        }
      });
    };
    nodeHasError(validationErrors);
    return hasError;
  };

  const onSubmit: FormContextValue["onSubmit"] = (action, event) => {
    clearNotifications();
    event.preventDefault();
    if (isSubmitting) return;
    if (!action.dangerouslyDisableShowLoading) setIsLoading(true);
    if (!action.noValidation) {
      const hasError = validateForm();
      if (hasError) {
        if (!action.dangerouslyDisableShowLoading) setIsLoading(false);
        if (!action.shouldHideInlineToasts && !shouldHideInlineToasts) {
          sendNegativeNotification(messages.errorSummary);
        }
        return;
      }
    }
    // define callback for fatal errors
    const handleErrors = (errors?: unknown) => {
      if (errors) {
        // errors can either be string, list of strings, or an object
        let all;
        let fieldErrors;
        if (typeof errors === "string") {
          // the string would be the global error
          all = errors;
          fieldErrors = {};
        } else if (Array.isArray(errors)) {
          all = errors.join(". ");
          fieldErrors = {};
        } else {
          // treat normally
          ({ __all__: all, ...fieldErrors } = errors as FormErrors);
        }
        if (typeof all === "string" && all.match(/[a-zA-Z0-9]$/)) {
          all += ".";
        }
        all = all || messages.errorGeneric;
        sendNotification({
          type: "negative",
          text: all as string,
        });
        setFormErrors(fieldErrors);
      }
    };
    const submitCallback = (errors?: unknown) => {
      setIsSubmitting("");
      if (!action.dangerouslyDisableShowLoading) setIsLoading(false);
      // trigger warning handling then error handling, set up as a callback chaing and not as
      // a promise chain as there's potentially a user prompt =(
      return (
        handleWarnings as (
          errors: unknown,
          action: FormAction,
          callback: (errors?: unknown) => void
        ) => void
      )(errors, action, handleErrors);
    };
    setIsSubmitting(action.label);
    action.onSubmit(submitCallback, event);
  };

  return (
    <FormContext.Provider
      value={{
        formID,
        data,
        dispatchData: dispatchWithOnChange,
        metadata,
        dispatchMetadata,
        validateForm,
        onSubmit,
        isSubmitting,
        setIsSubmitting,
      }}
    >
      {children}
    </FormContext.Provider>
  );
};

FormContextProvider.propTypes = {
  children: PropTypes.node,
  data: PropTypes.object,
  onChange: PropTypes.func,
  shouldHideAllToasts: PropTypes.bool,
  messages: PropTypes.object,
};

export const isObjectOrArray = (input: unknown) => {
  return input && (typeof input === "object" || Array.isArray(input));
};

export const mergeObjects = (base: unknown, override: unknown): unknown => {
  if (isObjectOrArray(base) && isObjectOrArray(override)) {
    for (const key in override as FormData) {
      (base as FormData)[key] = (override as FormData)[key];
    }
    return base;
  }
  return override === undefined ? base : override;
};

const ContextFormComponent = (
  {
    children,
    data,
    onChange,
    formID,
    nativeForm = true,
    /* native HTML form element props */
    action = "",
    autoComplete = "off",
    method = "",
    shouldHideAllToasts = false,
    confirmWithToastMessage = false,
    messages = DEFAULT_MESSAGES,
  }: ContextFormProps,
  ref: ForwardedRef<HTMLFormElement | HTMLDivElement>
) => {
  const formContainerRef = useRef<HTMLFormElement | HTMLDivElement | null>(null);
  // We need access to the node in the current component
  // as well as the parent component in some instances.
  // Setting the ref can either be with a useRef (which has a current)
  // or a function so here we are accounting for all cases.

  const setRef = (node: HTMLFormElement | HTMLDivElement | null) => {
    formContainerRef.current = node;
    if (typeof ref === "function") {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }
  };
  // When the form is inside of a Dialog component we want to
  // only show API errors.
  const isInDialog = useIsInDialog(formContainerRef);
  const provider = (
    <FormReviewWarningsContextProvider
      ignoreWarnings={data?.ignore_warnings}
      setIgnoreWarnings={(warnings) => onChange!({ ignore_warnings: warnings })}
      confirmWithToastMessage={confirmWithToastMessage}
      messages={messages}
    >
      <FormContextProvider
        data={data}
        onChange={onChange}
        formID={formID}
        shouldHideInlineToasts={isInDialog}
        shouldHideAllToasts={shouldHideAllToasts}
        messages={messages}
      >
        {children}
      </FormContextProvider>
    </FormReviewWarningsContextProvider>
  );
  return nativeForm ? (
    <form ref={setRef} action={action} autoComplete={autoComplete} method={method}>
      {provider}
    </form>
  ) : (
    <div ref={setRef}>{provider}</div>
  );
};

const ContextForm = forwardRef(ContextFormComponent) as ForwardRefExoticComponent<
  ContextFormProps & RefAttributes<HTMLFormElement | HTMLDivElement>
> & {
  FormContext: typeof FormContext;
  Field: typeof ContextFormField;
  Action: typeof Action;
  ActionBar: typeof ActionBar;
};

ContextForm.propTypes = {
  children: PropTypes.node,
  data: PropTypes.object,
  onChange: PropTypes.func,
  nativeForm: PropTypes.bool,
  action: PropTypes.string,
  autoComplete: PropTypes.string,
  method: PropTypes.string,
  shouldHideAllToasts: PropTypes.bool,
  /** Error and warning messages */
  messages: PropTypes.object,
};

ContextForm.FormContext = FormContext;
ContextForm.Field = ContextFormField;
ContextForm.Action = Action;
ContextForm.ActionBar = ActionBar;

export type { ContextFormProps } from "./types";
export type ContextFormFieldProps = React.ComponentProps<typeof ContextFormField>;
export type ContextFormActionProps = React.ComponentProps<typeof Action>;
export type ContextFormActionBarProps = React.ComponentProps<typeof ActionBar>;

export default ContextForm;
